'use client';
/** @jsxImportSource @emotion/react */

import React from 'react';

import type { SwitchProps as MuiSwitchProps } from '@mui/material/Switch';

import MuiSwitch from '@mui/material/Switch';

type ExcludedSwitchProps =
  | 'checkedIcon'
  | 'disableRipple'
  | 'icon'
  | 'inputRef';

interface WithLabel {
  /** lists the `id`s of the elements that describe the current element. It is used to establish a relationship between elements or groups and the text that describes them. */
  describedBy?: string;
  /** Provides meaningful label for accessibility purposes. Use when a visible label is not necessary */
  label: string;
  /** Id of element that labels the Switch. Use when providing own label component - inspecting the label component will reveal an `id` that matches the `aria-labelledby` attribute of the Switch `input`' */
  labelledBy?: never;
}

interface WithLabelledBy {
  /** lists the `id`s of the elements that describe the current element. It is used to establish a relationship between elements or groups and the text that describes them. */
  describedBy?: string;
  /** Provides meaningful label for accessibility purposes. Use when a visible label is not necessary */
  label?: never;
  /** Id of element that labels the Switch. Use when providing own label component - inspecting the label component will reveal an `id` that matches the `aria-labelledby` attribute of the Switch `input`' */
  labelledBy: string;
}

type BaseProps = Omit<MuiSwitchProps, ExcludedSwitchProps>;

type SwitchProps = BaseProps & (WithLabel | WithLabelledBy);

const Switch: React.FC<SwitchProps> = ({
  describedBy,
  label,
  labelledBy,
  ...props
}) => {
  const { checked, onChange, ...rest } = props;
  return (
    <MuiSwitch
      inputProps={{
        'aria-describedby': describedBy || undefined,
        'aria-label': labelledBy ? undefined : label,
        'aria-labelledby': labelledBy || undefined,
        role: 'switch',
      }}
      checked={checked}
      onChange={onChange}
      {...rest}
    />
  );
};

export { Switch };

Switch.displayName = 'Switch';
